import React, { useState } from "react";
import { Dialog } from "@material-tailwind/react";
import {
  EnvelopeIcon,
  PhoneIcon,
  XMarkIcon,
  SparklesIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { useReduxStore } from "../../hooks/useReduxStore";
import bgHomeS3 from "../../assets/landingPage/bg-home-s3.png";

// Curated high-impact House Ads for BAB Media when no promotional ads exist or to complement the stream
const HOUSE_PROMO_ADS = [
  {
    id: "house-1",
    isHouseAd: true,
    title: "Feature Your Brand on BAB Media",
    description:
      "Showcase your business, startup, or corporate brand to thousands of global creators and viewers. Benefit from 24/7 continuous ad streaming placements across all devices.",
    image:
      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=800&h=450",
    badge: "ADVERTISE HERE",
  },
  {
    id: "house-2",
    isHouseAd: true,
    title: "SoulFlow Records — Stream & Distribute Your Music",
    description:
      "Join the official music powerhouse of BAB Media. Publish original albums, soundtracks, and singles with global royalties and direct consumer reach.",
    image:
      "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&q=80&w=800&h=450",
    badge: "MUSIC SPOTLIGHT",
  },
  {
    id: "house-3",
    isHouseAd: true,
    title: "Mystio Global Logistics — Dispatch & Deliver Worldwide",
    description:
      "Intelligent fulfillment and fast logistics solutions for retailers, e-commerce, and global creators. Connect your supply chain with BAB Media.",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=800&h=450",
    badge: "LOGISTICS PARTNER",
  },
  {
    id: "house-4",
    isHouseAd: true,
    title: "Boss Amongst Bosses — Digital Publishing Ecosystem",
    description:
      "Become the CEO of your own content. Distribute e-books, audiobooks, and exclusive media directly to an engaged, high-value community.",
    image:
      "https://images.unsplash.com/photo-1457369804613-52c61a468e7d?auto=format&fit=crop&q=80&w=800&h=450",
    badge: "PUBLISHING HUB",
  },
];

// Helper to fix malformed asset URLs (e.g. double slashes from Laravel asset helper)
const sanitizeImageUrl = (url) => {
  if (!url) return "";
  return url.replace("public//storage", "public/storage");
};

export default function AdsStreamSectionNew() {
  const { getState } = useReduxStore();
  const { homeBanners } = getState("Home");

  const [selectedAd, setSelectedAd] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine active ads list: use live promotions from Redux store, or fall back to high-end house ads
  const rawAdsList =
    Array.isArray(homeBanners) && homeBanners.length > 0
      ? homeBanners
      : HOUSE_PROMO_ADS;

  // Seamless looping multiplier: duplicate list so continuous sliding marquee has zero gaps
  const loopMultiplier = rawAdsList.length < 5 ? 4 : 2;
  const slidingAds = Array.from({ length: loopMultiplier }).flatMap(
    () => rawAdsList
  );

  const handleCardClick = (ad) => {
    setSelectedAd(ad);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAd(null);
  };

  return (
    <section
      id="ads-stream"
      className="w-full py-20 px-0 md:px-0 lg:px-0 bg-cover bg-center relative overflow-hidden"
      style={{ backgroundImage: `url(${bgHomeS3})` }}
    >
      {/* Dark Ambient Overlay */}
      <div className="absolute inset-0 bg-black/75 pointer-events-none" />

      {/* Top Header */}
      <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center justify-center mb-10">
        <div className="inline-flex items-center gap-3 mb-2">
          {/* Glowing Amber Bullet */}
          <span className="w-2.5 h-2.5 rounded-full bg-secondary shadow-[0_0_12px_#FF8C21] animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-oswald text-white tracking-[0.2em] uppercase">
            CONTINUOUS ADS STREAM
          </h2>
        </div>
        <p className="text-gray-400 font-outfit text-sm sm:text-base max-w-2xl text-center leading-relaxed">
          Showcasing active sponsor advertisements running continuously across the platform.
        </p>
      </div>

      {/* Continuous Sliding Stream Container */}
      <div className="relative w-full overflow-hidden my-4">
        {/* Left & Right Smooth Vignette Fade Gradients */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-28 md:w-36 bg-gradient-to-r from-black via-black/80 to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-28 md:w-36 bg-gradient-to-l from-black via-black/80 to-transparent z-10" />

        {/* Marquee Track (Smooth Continuous Linear Slider) */}
        <div className="animate-continuous-stream flex items-center gap-5 sm:gap-6 py-4">
          {slidingAds.map((ad, index) => {
            const imgSrc = sanitizeImageUrl(ad.image);
            const key = `${ad.id || "ad"}-${index}`;

            return (
              <div
                key={key}
                onClick={() => handleCardClick(ad)}
                className="w-[300px] sm:w-[360px] md:w-[400px] h-[180px] sm:h-[210px] md:h-[230px] flex-shrink-0 rounded-2xl overflow-hidden relative border border-white/20 hover:border-secondary transition-all duration-300 group shadow-2xl bg-neutral-900/60 backdrop-blur-sm cursor-pointer hover:scale-[1.02] select-none"
              >
                {/* Banner Image */}
                <img
                  src={imgSrc}
                  alt={ad.title || "BAB Media Advertisement"}
                  className="w-full h-full object-cover rounded-2xl group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    // Fallback to high-resolution default promo image if CDN/image fails
                    e.currentTarget.src =
                      "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&q=80&w=800&h=450";
                  }}
                />

                {/* Badge for House Ad or Sponsored Placement */}
                <div className="absolute top-3 left-3 z-10 pointer-events-none">
                  <span className="px-2.5 py-1 rounded-md text-[11px] font-oswald tracking-widest font-semibold uppercase bg-black/70 backdrop-blur-md border border-white/20 text-secondary shadow-md">
                    {ad.badge || "SPONSORED"}
                  </span>
                </div>

                {/* Hover Gradient Overlay with Details */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 rounded-2xl">
                  <h3 className="text-white font-oswald text-base sm:text-lg font-bold tracking-wide uppercase line-clamp-1">
                    {ad.title}
                  </h3>
                  <p className="text-gray-300 font-outfit text-xs sm:text-sm line-clamp-2 mt-1">
                    {ad.description}
                  </p>
                  <div className="flex items-center gap-1.5 text-secondary text-xs font-oswald tracking-wider uppercase font-semibold mt-2">
                    <span>View Details</span>
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Brand Partnership Callout Banner (Bottom Container) */}
      <div className="relative z-10 max-w-6xl mx-auto mt-12 md:mt-16 p-6 sm:p-10 md:p-12 rounded-3xl border border-white/20 bg-neutral-950/70 backdrop-blur-md shadow-2xl">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          {/* Left Column: Heading and Value Proposition */}
          <div className="flex-1 text-center lg:text-left">
            <span className="text-secondary font-oswald tracking-[0.25em] text-xs sm:text-sm font-semibold uppercase inline-block mb-1">
              PARTNER WITH BOSS AMONGST BOSSES
            </span>
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold font-oswald text-white tracking-wide uppercase leading-tight mt-1 mb-4">
              STREAM YOUR BRAND ON BAB MEDIA
            </h3>
            <p className="text-gray-300 font-outfit text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
              Promote your original audio, videos, products, or corporate services to our global audience. Benefit from continuous banner ads and streaming placements throughout the platform.
            </p>
          </div>

          {/* Right Column: Contact Inquiry Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full lg:w-auto">
            {/* Email Inquiry Card */}
            <a
              href="mailto:babmedia2023@gmail.com?subject=Brand%20Sponsorship%20Inquiry%20-%20BAB%20Media"
              className="p-6 rounded-2xl border border-neutral-800 bg-neutral-900/60 hover:border-secondary hover:bg-neutral-900/90 transition-all duration-300 flex flex-col items-center justify-center text-center group min-w-[210px]"
            >
              <div className="w-12 h-12 rounded-xl bg-neutral-800/80 border border-neutral-700/60 flex items-center justify-center mb-3 group-hover:border-secondary group-hover:scale-110 transition-all">
                <EnvelopeIcon className="w-6 h-6 text-secondary" />
              </div>
              <span className="text-gray-400 font-oswald text-xs tracking-widest uppercase mb-1">
                EMAIL INQUIRY
              </span>
              <span className="text-white font-outfit font-semibold text-xs sm:text-sm hover:text-secondary transition-colors break-all">
                babmedia2023@gmail.com
              </span>
            </a>

            {/* Call & Stream Card */}
            <a
              href="tel:6097739620"
              className="p-6 rounded-2xl border border-neutral-800 bg-neutral-900/60 hover:border-secondary hover:bg-neutral-900/90 transition-all duration-300 flex flex-col items-center justify-center text-center group min-w-[210px]"
            >
              <div className="w-12 h-12 rounded-xl bg-neutral-800/80 border border-neutral-700/60 flex items-center justify-center mb-3 group-hover:border-secondary group-hover:scale-110 transition-all">
                <PhoneIcon className="w-6 h-6 text-secondary" />
              </div>
              <span className="text-gray-400 font-oswald text-xs tracking-widest uppercase mb-1">
                CALL & STREAM
              </span>
              <span className="text-white font-outfit font-semibold text-xs sm:text-sm hover:text-secondary transition-colors">
                609-773-9620
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Interactive Ad Detail Modal */}
      <Dialog
        open={isModalOpen}
        handler={handleCloseModal}
        size="md"
        className="bg-neutral-950 border border-neutral-800 shadow-2xl p-0 overflow-hidden text-white rounded-2xl"
      >
        {selectedAd && (
          <div className="relative flex flex-col max-h-[90vh]">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/70 border border-white/20 text-gray-300 hover:text-white hover:border-secondary flex items-center justify-center transition-all"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            {/* Banner Image Preview */}
            <div className="w-full h-56 sm:h-72 relative bg-neutral-900 overflow-hidden">
              <img
                src={sanitizeImageUrl(selectedAd.image)}
                alt={selectedAd.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
            </div>

            {/* Content Details */}
            <div className="p-6 sm:p-8 flex flex-col gap-4 overflow-y-auto">
              <div className="inline-flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-secondary" />
                <span className="text-secondary font-oswald text-xs tracking-widest uppercase font-semibold">
                  {selectedAd.badge || "ACTIVE SPONSOR PROMOTION"}
                </span>
              </div>

              <h4 className="text-2xl sm:text-3xl font-bold font-oswald text-white tracking-wide uppercase leading-snug">
                {selectedAd.title}
              </h4>

              <p className="text-gray-300 font-outfit text-sm sm:text-base leading-relaxed">
                {selectedAd.description}
              </p>

              {/* Action Buttons in Modal */}
              {/* <div className="flex flex-col sm:flex-row items-center gap-3 pt-4 border-t border-neutral-800">
                <a
                  href={`mailto:babmedia2023@gmail.com?subject=Inquiry%20regarding%20promotion:%20${encodeURIComponent(
                    selectedAd.title || ""
                  )}`}
                  className="w-full sm:flex-1 py-3 px-6 rounded-xl bg-secondary hover:bg-secondary/90 text-white font-oswald text-sm font-semibold tracking-wider uppercase text-center transition-all shadow-lg shadow-secondary/20"
                >
                  Contact About Offer
                </a>
                <a
                  href="tel:6097739620"
                  className="w-full sm:w-auto py-3 px-6 rounded-xl border border-neutral-700 hover:border-secondary text-gray-300 hover:text-white font-oswald text-sm font-semibold tracking-wider uppercase text-center transition-all"
                >
                  Call: 609-773-9620
                </a>
              </div> */}
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
}
